import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Alert,
  ActivityIndicator,
  I18nManager,
} from 'react-native';
import { CameraView, Camera, BarcodeScanningResult } from 'expo-camera';
import { Ionicons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import logger from '../utils/logger';

interface QRCodeScannerScreenProps {
  navigation: any;
  route: any;
}

export interface ScannedDeviceData {
  v?: number; // QR format version
  type?: string; // Should be "iot-edge-device"
  deviceType?: string;
  name?: string;
  serialNumber?: string;
  firmwareVersion?: string;
  mqttUsername?: string;
  mqttPassword?: string;
  siteId?: string;
  zoneId?: string;
  hasBattery?: boolean;
  mqtt?: {
    broker?: string;
    port?: number;
    sslPort?: number;
  };
}

export default function QRCodeScannerScreen({ navigation, route }: QRCodeScannerScreenProps) {
  const { t, i18n } = useTranslation();
  const { theme } = useTheme();
  const [hasPermission, setHasPermission] = useState<boolean | null>(null);
  const [scanned, setScanned] = useState(false);
  const [scanning, setScanning] = useState(true);
  const isArabic = i18n.language === 'ar';
  const isRTL = I18nManager.isRTL || isArabic;

  useEffect(() => {
    requestCameraPermission();
  }, []);

  const requestCameraPermission = async () => {
    try {
      const { status } = await Camera.requestCameraPermissionsAsync();
      setHasPermission(status === 'granted');

      if (status !== 'granted') {
        Alert.alert(
          isArabic ? 'رفض الإذن' : 'Permission Denied',
          isArabic
            ? 'يحتاج التطبيق إلى إذن الكاميرا لمسح رموز QR'
            : 'Camera permission is required to scan QR codes',
          [
            {
              text: isArabic ? 'حسناً' : 'OK',
              onPress: () => navigation.goBack(),
            },
          ],
        );
      }
    } catch (error) {
      logger.error('Failed to request camera permission:', error);
      Alert.alert(
        isArabic ? 'خطأ' : 'Error',
        isArabic ? 'فشل طلب إذن الكاميرا' : 'Failed to request camera permission',
      );
      navigation.goBack();
    }
  };

  const handleBarCodeScanned = ({ data }: BarcodeScanningResult) => {
    if (scanned) return;

    setScanned(true);
    setScanning(false);

    try {
      // Parse QR code data
      const deviceData: ScannedDeviceData = JSON.parse(data);

      // Validate QR code format
      if (deviceData.v === 1 && deviceData.type === 'iot-edge-device') {
        // New factory format
        if (!deviceData.name || !deviceData.serialNumber) {
          throw new Error('Invalid QR code: Missing name or serial number');
        }
      } else {
        // Legacy format - validate required fields
        if (!deviceData.deviceType || !deviceData.name) {
          throw new Error('Invalid QR code: Missing required fields');
        }
      }

      // Build display info
      const displayInfo = deviceData.serialNumber
        ? `${deviceData.name}\nS/N: ${deviceData.serialNumber}`
        : deviceData.name;

      // Show success message
      Alert.alert(
        isArabic ? 'تم المسح بنجاح' : 'Scanned Successfully',
        isArabic ? `تم مسح جهاز:\n${displayInfo}` : `Scanned device:\n${displayInfo}`,
        [
          {
            text: isArabic ? 'استخدام البيانات' : 'Use Data',
            onPress: () => {
              // Navigate back and pass data
              if (route.params?.onScan) {
                route.params.onScan(deviceData);
              }
              navigation.goBack();
            },
          },
          {
            text: isArabic ? 'مسح مرة أخرى' : 'Scan Again',
            onPress: () => {
              setScanned(false);
              setScanning(true);
            },
          },
        ],
      );
    } catch (error) {
      logger.error('Failed to parse QR code:', error);
      Alert.alert(
        isArabic ? 'رمز QR غير صالح' : 'Invalid QR Code',
        isArabic ? 'الرجاء مسح رمز QR صالح للجهاز' : 'Please scan a valid device QR code',
        [
          {
            text: isArabic ? 'إعادة المحاولة' : 'Try Again',
            onPress: () => {
              setScanned(false);
              setScanning(true);
            },
          },
          {
            text: isArabic ? 'إلغاء' : 'Cancel',
            style: 'cancel',
            onPress: () => navigation.goBack(),
          },
        ],
      );
    }
  };

  const handleManualEntry = () => {
    navigation.goBack();
  };

  if (hasPermission === null) {
    return (
      <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
        <Text style={[styles.loadingText, { color: theme.colors.text }]}>
          {isArabic ? 'جاري طلب إذن الكاميرا...' : 'Requesting camera permission...'}
        </Text>
      </View>
    );
  }

  if (hasPermission === false) {
    return (
      <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
        <Ionicons name="camera-outline" size={64} color={theme.colors.textSecondary} />
        <Text style={[styles.errorText, { color: theme.colors.text }]}>
          {isArabic ? 'لا يوجد إذن للوصول للكاميرا' : 'No access to camera'}
        </Text>
        <TouchableOpacity
          style={[styles.button, { backgroundColor: theme.colors.primary }]}
          onPress={() => navigation.goBack()}
        >
          <Text style={styles.buttonText}>{isArabic ? 'رجوع' : 'Go Back'}</Text>
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <CameraView
        onBarcodeScanned={scanning ? handleBarCodeScanned : undefined}
        style={StyleSheet.absoluteFillObject}
        barcodeScannerSettings={{
          barcodeTypes: ['qr'],
        }}
      />

      {/* Overlay */}
      <View style={styles.overlay}>
        {/* Top bar */}
        <View style={[styles.topBar, { backgroundColor: 'rgba(0,0,0,0.7)' }]}>
          <TouchableOpacity style={styles.closeButton} onPress={() => navigation.goBack()}>
            <Ionicons name="close" size={28} color="#FFF" />
          </TouchableOpacity>
          <Text style={styles.title}>{isArabic ? 'مسح رمز QR' : 'Scan QR Code'}</Text>
          <View style={{ width: 40 }} />
        </View>

        {/* Scanning frame */}
        <View style={styles.scanArea}>
          <View style={styles.scanFrame}>
            {/* Corner decorations */}
            <View style={[styles.corner, styles.topLeft]} />
            <View style={[styles.corner, styles.topRight]} />
            <View style={[styles.corner, styles.bottomLeft]} />
            <View style={[styles.corner, styles.bottomRight]} />

            {scanning && (
              <View style={styles.scanLine}>
                <View style={styles.scanLineInner} />
              </View>
            )}
          </View>
        </View>

        {/* Bottom instructions */}
        <View style={[styles.bottomBar, { backgroundColor: 'rgba(0,0,0,0.7)' }]}>
          <Ionicons name="qr-code-outline" size={48} color="#FFF" />
          <Text style={styles.instructionText}>
            {isArabic ? 'ضع رمز QR داخل الإطار للمسح' : 'Place QR code inside the frame to scan'}
          </Text>

          <TouchableOpacity
            style={[styles.manualButton, { borderColor: '#FFF' }]}
            onPress={handleManualEntry}
          >
            <Ionicons name="create-outline" size={20} color="#FFF" />
            <Text style={styles.manualButtonText}>{isArabic ? 'إدخال يدوي' : 'Manual Entry'}</Text>
          </TouchableOpacity>

          {!scanning && (
            <TouchableOpacity
              style={[styles.retryButton, { backgroundColor: theme.colors.primary }]}
              onPress={() => {
                setScanned(false);
                setScanning(true);
              }}
            >
              <Ionicons name="refresh" size={20} color="#FFF" />
              <Text style={styles.buttonText}>{isArabic ? 'مسح مرة أخرى' : 'Scan Again'}</Text>
            </TouchableOpacity>
          )}
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
  },
  errorText: {
    marginTop: 16,
    fontSize: 16,
    textAlign: 'center',
    paddingHorizontal: 32,
  },
  button: {
    marginTop: 24,
    paddingHorizontal: 32,
    paddingVertical: 12,
    borderRadius: 12,
  },
  buttonText: {
    color: '#FFF',
    fontSize: 16,
    fontWeight: '600',
  },
  overlay: {
    flex: 1,
    justifyContent: 'space-between',
  },
  topBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: 50,
    paddingBottom: 16,
    paddingHorizontal: 16,
  },
  closeButton: {
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  title: {
    color: '#FFF',
    fontSize: 20,
    fontWeight: '700',
  },
  scanArea: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  scanFrame: {
    width: 280,
    height: 280,
    position: 'relative',
  },
  corner: {
    position: 'absolute',
    width: 40,
    height: 40,
    borderColor: '#FFF',
  },
  topLeft: {
    top: 0,
    left: 0,
    borderTopWidth: 4,
    borderLeftWidth: 4,
  },
  topRight: {
    top: 0,
    right: 0,
    borderTopWidth: 4,
    borderRightWidth: 4,
  },
  bottomLeft: {
    bottom: 0,
    left: 0,
    borderBottomWidth: 4,
    borderLeftWidth: 4,
  },
  bottomRight: {
    bottom: 0,
    right: 0,
    borderBottomWidth: 4,
    borderRightWidth: 4,
  },
  scanLine: {
    position: 'absolute',
    width: '100%',
    height: 2,
    top: '50%',
    overflow: 'hidden',
  },
  scanLineInner: {
    width: '100%',
    height: '100%',
    backgroundColor: '#00FF00',
    shadowColor: '#00FF00',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 1,
    shadowRadius: 10,
    boxShadow: '0px 0px 10px rgba(0, 255, 0, 1)',
  },
  bottomBar: {
    padding: 24,
    alignItems: 'center',
    gap: 16,
  },
  instructionText: {
    color: '#FFF',
    fontSize: 16,
    textAlign: 'center',
    fontWeight: '500',
  },
  manualButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 12,
    borderWidth: 2,
  },
  manualButtonText: {
    color: '#FFF',
    fontSize: 16,
    fontWeight: '600',
  },
  retryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 12,
  },
});
